import { updateTenantBalance } from '@saaga/shared/store/slices/billing.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { pushGtmPurchase } from '@saaga/shared/utils/gtm.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { useBillingClient } from './use-billing-client.hook'

export interface UseTopUpReturn {
  topUp: (amountDollars: number) => Promise<boolean>
  isLoading: boolean
  error: string | null
}

const trackTopUpSuccess = (amountDollars: number, invoiceId: string): void => {
  trackEvent('balance_topped_up', { amount: amountDollars, transactionId: invoiceId })
  pushGtmPurchase({
    transactionId: invoiceId,
    value: amountDollars,
    currency: 'USD',
    itemList: [
      { itemId: 'balance_top_up', itemName: 'Balance Top-Up', price: amountDollars, quantity: 1 },
    ],
  })
}

export const useTopUp = (): UseTopUpReturn => {
  const dispatch = useAppDispatch()
  const { topUp: topUpApi } = useBillingClient()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const topUp = useCallback(
    async (amountDollars: number): Promise<boolean> => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await topUpApi(amountDollars)

        if (response.paymentPending && response.paymentUrl) {
          window.location.href = response.paymentUrl
          return true
        }

        if (!response.success) {
          const errorMessage = 'Payment failed. Please try again.'
          setError(errorMessage)
          toast.error(errorMessage)
          return false
        }

        toast.success(`Successfully added $${amountDollars} to your balance`)
        trackTopUpSuccess(amountDollars, response.invoiceId)

        if (response.balance) {
          dispatch(updateTenantBalance(response.balance))
        }

        return true
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process payment'
        setError(errorMessage)
        toast.error(errorMessage)
        logger.error('Failed to top up', { error: err, amountDollars })
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [topUpApi, dispatch]
  )

  return { topUp, isLoading, error }
}
